import type { ReactNode } from "react"
import { useMemo, useRef } from "react"
import { useDropzone } from "react-dropzone"
import { LuLoaderCircle, LuUpload } from "react-icons/lu"

import { cn } from "~/v2/utils/cn"

type DropzoneAccept = string | Record<string, string[]>

type RejectedFile = {
  errors: Array<{ code: string }>
  file: File
}

type DropzoneProps = {
  accept?: DropzoneAccept
  children?: ReactNode
  className?: string
  disabled?: boolean
  loading?: boolean
  onDrop?: (files: File[]) => void
  onError?: (files: RejectedFile[] | any[]) => void
  title?: string
}

const getAcceptAttribute = (accept?: DropzoneAccept) => {
  if (!accept) {
    return undefined
  }

  if (typeof accept === "string") {
    return accept
  }

  return Object.entries(accept)
    .flatMap(([mimeType, extensions]) => [mimeType, ...(extensions || [])])
    .join(",")
}

const fileMatchesMimeType = (file: File, mimeType: string) => {
  if (mimeType.endsWith("/*")) {
    const prefix = mimeType.slice(0, mimeType.indexOf("/"))
    return file.type.startsWith(`${prefix}/`)
  }

  return file.type === mimeType
}

const fileMatchesAccept = (file: File, accept?: DropzoneAccept) => {
  if (!accept) {
    return true
  }

  if (typeof accept === "string") {
    return accept.split(",").some((value) => {
      const rule = value.trim()
      if (!rule) {
        return false
      }
      if (rule.startsWith(".")) {
        return file.name.toLowerCase().endsWith(rule.toLowerCase())
      }
      return fileMatchesMimeType(file, rule)
    })
  }

  return Object.entries(accept).some(([mimeType, extensions]) => {
    const matchesMimeType = mimeType
      ? fileMatchesMimeType(file, mimeType)
      : false
    const matchesExtension =
      (extensions || []).length > 0
        ? (extensions || []).some((extension) =>
            file.name.toLowerCase().endsWith(extension.toLowerCase()),
          )
        : false

    return matchesMimeType || matchesExtension
  })
}

const Dropzone = ({
  accept,
  children,
  className,
  disabled = false,
  loading = false,
  onDrop,
  onError,
  title = "Drop file here or click to browse",
}: DropzoneProps) => {
  const hasChildren = Boolean(children)
  const inputRef = useRef<HTMLInputElement | null>(null)
  const acceptAttribute = useMemo(() => getAcceptAttribute(accept), [accept])

  const handleFiles = (fileList: FileList | null) => {
    const files = Array.from(fileList || [])
    if (!files.length) {
      return
    }

    const [file] = files

    if (!fileMatchesAccept(file, accept)) {
      onError?.([
        {
          errors: [{ code: "file-invalid-type" }],
          file,
        },
      ])
      return
    }

    onDrop?.([file])
  }

  const { getInputProps, getRootProps } = useDropzone({
    accept: acceptAttribute,
    disabled: disabled || loading,
    maxFiles: 1,
    multiple: false,
    noClick: hasChildren,
    noDrag: hasChildren,
    noDragEventsBubbling: hasChildren,
    onDrop: (acceptedFiles, rejectedFiles) => {
      if (rejectedFiles.length > 0) {
        onError?.(rejectedFiles)
        return
      }

      onDrop?.(acceptedFiles)
    },
  })

  if (hasChildren) {
    return (
      <div className={cn("relative", className)}>
        <input
          accept={acceptAttribute}
          className="hidden"
          disabled={disabled || loading}
          onChange={(event) => {
            handleFiles(event.target.files)
            event.target.value = ""
          }}
          ref={inputRef}
          type="file"
        />
        {loading && (
          <div className="absolute inset-0 z-10 flex items-center justify-center rounded-md bg-background/80">
            <LuLoaderCircle className="h-5 w-5 animate-spin text-primary" />
          </div>
        )}
        <div
          className={cn(
            "relative inline-flex",
            !(disabled || loading) && "cursor-pointer",
            (disabled || loading) && "pointer-events-none",
            className,
          )}
          onClick={() => {
            if (!(disabled || loading)) {
              inputRef.current?.click()
            }
          }}
          onKeyDown={(event) => {
            if (disabled || loading) {
              return
            }

            if (event.key === "Enter" || event.key === " ") {
              event.preventDefault()
              inputRef.current?.click()
            }
          }}
          role="button"
          tabIndex={disabled || loading ? -1 : 0}
        >
          {children}
        </div>
      </div>
    )
  }

  return (
    <div
      {...getRootProps()}
      className={cn(
        "flex min-h-[140px] cursor-pointer flex-col items-center justify-center rounded-md border border-dashed border-border bg-muted/20 px-4 text-center transition-colors hover:bg-muted/30",
        (disabled || loading) && "cursor-not-allowed opacity-70",
        className,
      )}
    >
      <input {...getInputProps()} />
      {loading ? (
        <>
          <LuLoaderCircle className="mb-3 h-5 w-5 animate-spin text-primary" />
          <div className="text-sm font-medium text-foreground">
            Uploading...
          </div>
        </>
      ) : (
        <>
          <LuUpload className="mb-3 h-5 w-5 text-primary" />
          <div className="text-sm font-medium text-foreground">{title}</div>
        </>
      )}
    </div>
  )
}

export default Dropzone
